import {
  CHUNK_SIZE,
  chunkKeyInt,
  createNoiseGenerator,
  TILE_SIZE,
  TileType,
  type TileTypeValue,
} from '@dreamgame/shared';
import { Container, Filter, GlProgram, Graphics } from 'pixi.js';
import { IS_SAFARI } from '../../platform/browser';
import { type ChunkWithObjects, getAllChunks } from '../../state/chunks';
import { getCamera, getScreenDimensions } from '../Camera';
import type { VisibleChunk } from './types';

// ============================================================================
// FoliageWindLayer - Animated grass blades with GLSL wind shader
// ============================================================================
// Draws small grass blade triangles on grass/forest tiles, cached per-chunk.
// A GLSL fragment shader applies multi-frequency sine wave displacement for
// a traveling wind effect. Safari fallback: static foliage (no shader).
// ============================================================================

let container: Container;

// Tile types that get foliage blades
const GRASS_TILES: ReadonlySet<TileTypeValue> = new Set([TileType.GRASS, TileType.FOREST_GRASS]);

const FOREST_TILES: ReadonlySet<TileTypeValue> = new Set([
  TileType.FOREST_FLOOR,
  TileType.PINE_NEEDLES,
]);

// Blade palettes (base + highlighted tip colors)
const MEADOW_BASE_COLORS = [0x3b6e28, 0x47782e, 0x4f8335, 0x5b8d3e, 0x679747] as const;
const MEADOW_TIP_COLORS = [0x79ad52, 0x86b95a, 0x90bf63, 0x9ccc70] as const;
const FOREST_BASE_COLORS = [0x2d4e23, 0x315627, 0x365e2b, 0x3c6631, 0x426f35] as const;
const FOREST_TIP_COLORS = [0x4f7d3a, 0x5b8a43, 0x66974d, 0x70a255] as const;

const BLADE_BASE_ALPHA = 0.82;
const BLADE_TIP_ALPHA = 0.42;
const BLADE_SHADOW_ALPHA = 0.24;

// Blade geometry
const BLADE_MIN_HEIGHT = 7;
const BLADE_MAX_HEIGHT = 14;
const BLADE_HALF_WIDTH_MIN = 0.95;
const BLADE_HALF_WIDTH_MAX = 2.15;
const BLADE_CURVE_MAX = 2.6;

// Tuft composition
const GRASS_TUFTS_MIN = 2;
const GRASS_TUFTS_MAX = 4;
const FOREST_TUFTS_MIN = 1;
const FOREST_TUFTS_MAX = 3;
const BLADES_PER_TUFT_MIN = 2;
const BLADES_PER_TUFT_MAX = 5;
const FLOWER_PATCH_CHANCE = 34; // /1000 on dense meadow tiles

// Low-frequency density field for organic grass clumps / clearings
const _foliageDensityNoise = createNoiseGenerator({ seed: 9301 });
const _foliagePatchNoise = createNoiseGenerator({ seed: 9317 });
const _foliageSpeciesNoise = createNoiseGenerator({ seed: 9329 });
const _foliageTuftNoise = createNoiseGenerator({ seed: 9343 });

// Per-chunk cache (same pattern as TileLayer)
interface CachedFoliageEntry {
  graphics: Graphics;
  tilesRef: Uint8Array;
}

const _chunkCache = new Map<number, CachedFoliageEntry>();
const _usedKeys = new Set<number>();

// PERFORMANCE: Preallocated temp for chunkKeyInt
const _chunkKeyTemp = { x: 0, y: 0 };

// Deterministic spatial hash (same primes as ForestEffectsLayer)
const foliageHash = ({
  tileX,
  tileY,
  seed,
}: {
  tileX: number;
  tileY: number;
  seed: number;
}): number => {
  return (((tileX * 48271 + seed * 7) ^ (tileY * 16807)) >>> 0) % 1000;
};

const foliageDensity = ({
  worldTileX,
  worldTileY,
}: {
  worldTileX: number;
  worldTileY: number;
}): number => {
  return (_foliageDensityNoise.noise2D({ x: worldTileX * 0.09, y: worldTileY * 0.09 }) + 1) / 2;
};

const FOLIAGE_PATCH_KEYS = {
  MEADOW_LUSH: 'meadow_lush',
  MEADOW_MIXED: 'meadow_mixed',
  MEADOW_DRY: 'meadow_dry',
  FOREST_SOFT: 'forest_soft',
  FOREST_NEEDLE: 'forest_needle',
} as const;

type FoliagePatchKey = (typeof FOLIAGE_PATCH_KEYS)[keyof typeof FOLIAGE_PATCH_KEYS];

interface FoliagePatchProfile {
  readonly basePalette: ReadonlyArray<number>;
  readonly tipPalette: ReadonlyArray<number>;
  readonly tuftMin: number;
  readonly tuftMax: number;
  readonly bladesMin: number;
  readonly bladesMax: number;
  readonly heightBias: number;
  readonly widthBias: number;
  readonly flowerChance: number; // 0..1000
}

const FOLIAGE_PATCH_PROFILES: Record<FoliagePatchKey, FoliagePatchProfile> = {
  [FOLIAGE_PATCH_KEYS.MEADOW_LUSH]: {
    basePalette: MEADOW_BASE_COLORS,
    tipPalette: MEADOW_TIP_COLORS,
    tuftMin: 3,
    tuftMax: 5,
    bladesMin: 3,
    bladesMax: 5,
    heightBias: 1.15,
    widthBias: 1.1,
    flowerChance: FLOWER_PATCH_CHANCE,
  },
  [FOLIAGE_PATCH_KEYS.MEADOW_MIXED]: {
    basePalette: MEADOW_BASE_COLORS,
    tipPalette: MEADOW_TIP_COLORS,
    tuftMin: GRASS_TUFTS_MIN,
    tuftMax: GRASS_TUFTS_MAX,
    bladesMin: BLADES_PER_TUFT_MIN,
    bladesMax: BLADES_PER_TUFT_MAX,
    heightBias: 1,
    widthBias: 1,
    flowerChance: FLOWER_PATCH_CHANCE / 2,
  },
  [FOLIAGE_PATCH_KEYS.MEADOW_DRY]: {
    basePalette: [0x5d6f38, 0x6f7a42, 0x7c8549],
    tipPalette: [0x96a65f, 0xa7b56b],
    tuftMin: 1,
    tuftMax: 3,
    bladesMin: 2,
    bladesMax: 4,
    heightBias: 0.88,
    widthBias: 0.92,
    flowerChance: 8,
  },
  [FOLIAGE_PATCH_KEYS.FOREST_SOFT]: {
    basePalette: FOREST_BASE_COLORS,
    tipPalette: FOREST_TIP_COLORS,
    tuftMin: FOREST_TUFTS_MIN,
    tuftMax: FOREST_TUFTS_MAX,
    bladesMin: 2,
    bladesMax: 4,
    heightBias: 0.94,
    widthBias: 1.06,
    flowerChance: 0,
  },
  [FOLIAGE_PATCH_KEYS.FOREST_NEEDLE]: {
    basePalette: [0x2a4620, 0x2e4f24, 0x335628, 0x385e2d],
    tipPalette: [0x476b32, 0x50763a, 0x5a8143],
    tuftMin: 1,
    tuftMax: 2,
    bladesMin: 2,
    bladesMax: 4,
    heightBias: 0.85,
    widthBias: 0.9,
    flowerChance: 0,
  },
};

const patchValue = ({
  worldTileX,
  worldTileY,
}: {
  worldTileX: number;
  worldTileY: number;
}): number => {
  return (_foliagePatchNoise.noise2D({ x: worldTileX * 0.03, y: worldTileY * 0.03 }) + 1) / 2;
};

const speciesValue = ({
  worldTileX,
  worldTileY,
}: {
  worldTileX: number;
  worldTileY: number;
}): number => {
  return (_foliageSpeciesNoise.noise2D({ x: worldTileX * 0.08, y: worldTileY * 0.08 }) + 1) / 2;
};

const tuftClusterValue = ({
  worldTileX,
  worldTileY,
}: {
  worldTileX: number;
  worldTileY: number;
}): number => {
  return (_foliageTuftNoise.noise2D({ x: worldTileX * 0.15, y: worldTileY * 0.15 }) + 1) / 2;
};

// ============================================================================
// GLSL Wind Shader
// ============================================================================

const WIND_VERTEX_SHADER = `
in vec2 aPosition;
out vec2 vTextureCoord;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;

vec4 filterVertexPosition(void) {
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
    return vec4(position, 0.0, 1.0);
}

vec2 filterTextureCoord(void) {
    return aPosition * (uOutputFrame.zw * uInputSize.zw);
}

void main(void) {
    gl_Position = filterVertexPosition();
    vTextureCoord = filterTextureCoord();
}
`;

const WIND_FRAGMENT_SHADER = `
precision highp float;

in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform vec4 uInputSize;

uniform float uTime;
uniform float uWindSpeed;
uniform float uWindStrength;
uniform float uGustStrength;
uniform float uFlutterStrength;
uniform vec2 uWorldOffset;
uniform vec2 uViewportSize;

float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(
        mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
        mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x),
        f.y
    );
}

float fbm(vec2 p) {
    float total = 0.0;
    float amplitude = 0.5;
    mat2 rot = mat2(0.84, 0.54, -0.54, 0.84);
    for (int i = 0; i < 3; i++) {
        total += vnoise(p) * amplitude;
        p = rot * p * 2.02 + vec2(27.3, 91.7);
        amplitude *= 0.5;
    }
    return total;
}

void main() {
    vec2 worldPos = vTextureCoord * uViewportSize + uWorldOffset;
    float t = uTime * uWindSpeed;

    // Coherent base wind + finer flutter
    float phase = worldPos.x * 0.064 + worldPos.y * 0.014 + t;
    float primary = sin(phase) * 0.52;
    float secondary = sin(phase * 1.93 + 1.7) * 0.27;
    float diagonal = sin((worldPos.x + worldPos.y) * 0.038 + t * 0.62) * 0.18;
    float flutter = sin(worldPos.y * 0.18 + t * 1.7 + phase * 0.5) * 0.19;

    // Low-frequency gust map creates traveling gust patches
    float gustField = fbm(worldPos * 0.012 + vec2(t * 0.18, -t * 0.1));
    float gust = smoothstep(0.58, 0.92, gustField);

    float totalDisplacement =
        (primary + secondary + diagonal) * (uWindStrength + gust * uGustStrength) +
        flutter * uFlutterStrength;

    // Apply predominantly horizontal displacement with slight vertical response
    vec2 uv = vTextureCoord;
    uv.x += totalDisplacement * uInputSize.z;
    uv.y += totalDisplacement * 0.14 * uInputSize.w;

    finalColor = texture(uTexture, uv);
}
`;

// PERFORMANCE: Preallocated uniform arrays
const _worldOffset = new Float32Array([0, 0]);
const _viewportSize = new Float32Array([1, 1]);

let _windFilter: Filter | null = null;
const ENABLE_WIND_SHADER = !IS_SAFARI;

// ============================================================================
// Layer Creation
// ============================================================================

export const createFoliageWindLayer = (): Container => {
  container = new Container();

  if (ENABLE_WIND_SHADER) {
    const glProgram = GlProgram.from({
      vertex: WIND_VERTEX_SHADER,
      fragment: WIND_FRAGMENT_SHADER,
      name: 'foliage-wind',
    });

    _windFilter = new Filter({
      glProgram,
      resources: {
        windUniforms: {
          uTime: { value: 0, type: 'f32' },
          uWindSpeed: { value: 1.5, type: 'f32' },
          uWindStrength: { value: 2.2, type: 'f32' },
          uGustStrength: { value: 1.15, type: 'f32' },
          uFlutterStrength: { value: 0.95, type: 'f32' },
          uWorldOffset: { value: _worldOffset, type: 'vec2<f32>' },
          uViewportSize: { value: _viewportSize, type: 'vec2<f32>' },
        },
      },
      padding: 4,
    });

    container.filters = [_windFilter];
  }

  return container;
};

// ============================================================================
// Per-Chunk Foliage Drawing
// ============================================================================

const resolvePatchKey = ({
  tileType,
  worldTileX,
  worldTileY,
}: {
  tileType: TileTypeValue;
  worldTileX: number;
  worldTileY: number;
}): FoliagePatchKey => {
  const density = foliageDensity({ worldTileX, worldTileY });
  const patch = patchValue({ worldTileX, worldTileY });
  const species = speciesValue({ worldTileX, worldTileY });

  if (FOREST_TILES.has(tileType)) {
    if (tileType === TileType.PINE_NEEDLES || patch < 0.33 || species < 0.3) {
      return FOLIAGE_PATCH_KEYS.FOREST_NEEDLE;
    }
    return FOLIAGE_PATCH_KEYS.FOREST_SOFT;
  }

  if (patch < 0.24) {
    return FOLIAGE_PATCH_KEYS.MEADOW_DRY;
  }
  if (patch > 0.74 || density > 0.7) {
    return FOLIAGE_PATCH_KEYS.MEADOW_LUSH;
  }
  return FOLIAGE_PATCH_KEYS.MEADOW_MIXED;
};

const drawCurvedBlade = ({
  g,
  rootX,
  rootY,
  height,
  halfWidth,
  lean,
  baseColor,
  tipColor,
}: {
  g: Graphics;
  rootX: number;
  rootY: number;
  height: number;
  halfWidth: number;
  lean: number;
  baseColor: number;
  tipColor: number;
}): void => {
  const tipX = rootX + lean;
  const tipY = rootY - height;
  const midY = rootY - height * 0.58;
  const leftBaseX = rootX - halfWidth;
  const rightBaseX = rootX + halfWidth;
  const leftMidX = rootX - halfWidth * 0.42 + lean * 0.36;
  const rightMidX = rootX + halfWidth * 0.42 + lean * 0.36;

  g.setFillStyle({ color: baseColor, alpha: BLADE_BASE_ALPHA });
  g.moveTo(leftBaseX, rootY);
  g.lineTo(leftMidX, midY);
  g.lineTo(tipX, tipY);
  g.lineTo(rightMidX, midY);
  g.lineTo(rightBaseX, rootY);
  g.closePath();
  g.fill();

  g.setFillStyle({ color: 0x1e2f18, alpha: BLADE_SHADOW_ALPHA });
  g.ellipse(rootX + lean * 0.1, rootY + 0.4, halfWidth * 0.7 + 0.4, 0.9);
  g.fill();

  g.setFillStyle({ color: tipColor, alpha: BLADE_TIP_ALPHA });
  g.ellipse(
    tipX + lean * 0.08,
    tipY + height * 0.24,
    Math.max(0.65, halfWidth * 0.32),
    Math.max(1.1, height * 0.2)
  );
  g.fill();
};

const drawTinyFlower = ({
  g,
  x,
  y,
  variant,
}: {
  g: Graphics;
  x: number;
  y: number;
  variant: number;
}): void => {
  const petalColor = variant % 2 === 0 ? 0xd7c37a : 0xe6d8a8;
  const centerColor = 0x8f7a3d;

  g.setFillStyle({ color: petalColor, alpha: 0.55 });
  g.circle(x - 0.9, y, 0.8);
  g.fill();
  g.circle(x + 0.9, y, 0.8);
  g.fill();
  g.circle(x, y - 0.85, 0.78);
  g.fill();
  g.circle(x, y + 0.85, 0.78);
  g.fill();

  g.setFillStyle({ color: centerColor, alpha: 0.72 });
  g.circle(x, y, 0.6);
  g.fill();
};

const drawTileFoliage = ({
  g,
  tileType,
  worldTileX,
  worldTileY,
  worldX,
  worldY,
}: {
  g: Graphics;
  tileType: TileTypeValue;
  worldTileX: number;
  worldTileY: number;
  worldX: number;
  worldY: number;
}): void => {
  const patchKey = resolvePatchKey({ tileType, worldTileX, worldTileY });
  const profile = FOLIAGE_PATCH_PROFILES[patchKey];
  if (!profile) {
    return;
  }

  const density = foliageDensity({ worldTileX, worldTileY });
  const cluster = tuftClusterValue({ worldTileX, worldTileY });
  const gate = foliageHash({ tileX: worldTileX, tileY: worldTileY, seed: 211 });
  const spawnThreshold = 180 + Math.floor((density * 0.7 + cluster * 0.3) * 620);
  if (gate > spawnThreshold) {
    return;
  }

  const tuftRange = profile.tuftMax - profile.tuftMin + 1;
  const baseTuftCount =
    profile.tuftMin +
    (foliageHash({ tileX: worldTileX, tileY: worldTileY, seed: 223 }) % tuftRange);
  const tuftCount = Math.max(
    1,
    Math.round(baseTuftCount * (0.75 + density * 0.6 + cluster * 0.35))
  );

  for (let t = 0; t < tuftCount; t++) {
    const tuftSeed = t * 41;
    const tx = foliageHash({ tileX: worldTileX, tileY: worldTileY, seed: 233 + tuftSeed });
    const ty = foliageHash({ tileX: worldTileX, tileY: worldTileY, seed: 241 + tuftSeed });
    const baseX = worldX + 2 + (tx % (TILE_SIZE - 4));
    const baseY = worldY + 14 + (ty % (TILE_SIZE - 13));

    const bladesRange = profile.bladesMax - profile.bladesMin + 1;
    const bladesInTuft =
      profile.bladesMin +
      (foliageHash({ tileX: worldTileX, tileY: worldTileY, seed: 251 + tuftSeed }) % bladesRange);

    for (let b = 0; b < bladesInTuft; b++) {
      const bladeSeed = tuftSeed + b * 13;
      const offsetSeed = foliageHash({
        tileX: worldTileX,
        tileY: worldTileY,
        seed: 263 + bladeSeed,
      });
      const heightSeed = foliageHash({
        tileX: worldTileX,
        tileY: worldTileY,
        seed: 271 + bladeSeed,
      });
      const widthSeed = foliageHash({
        tileX: worldTileX,
        tileY: worldTileY,
        seed: 277 + bladeSeed,
      });
      const leanSeed = foliageHash({ tileX: worldTileX, tileY: worldTileY, seed: 283 + bladeSeed });
      const colorSeed = foliageHash({
        tileX: worldTileX,
        tileY: worldTileY,
        seed: 293 + bladeSeed,
      });
      const tipSeed = foliageHash({ tileX: worldTileX, tileY: worldTileY, seed: 307 + bladeSeed });

      const offsetX = (offsetSeed % 9) - 4;
      const offsetY = Math.floor(offsetSeed / 11) % 3;
      const heightNoise = heightSeed / 1000;
      const widthNoise = widthSeed / 1000;
      const height = Math.round(
        (BLADE_MIN_HEIGHT + heightNoise * (BLADE_MAX_HEIGHT - BLADE_MIN_HEIGHT)) *
          profile.heightBias
      );
      const halfWidth =
        (BLADE_HALF_WIDTH_MIN + widthNoise * (BLADE_HALF_WIDTH_MAX - BLADE_HALF_WIDTH_MIN)) *
        profile.widthBias;
      const lean = ((leanSeed % 11) - 5) * (BLADE_CURVE_MAX / 5);
      const baseColor = profile.basePalette[colorSeed % profile.basePalette.length] ?? 0x3f6f2a;
      const tipColor = profile.tipPalette[tipSeed % profile.tipPalette.length] ?? 0x83b85c;

      drawCurvedBlade({
        g,
        rootX: baseX + offsetX,
        rootY: baseY + offsetY,
        height,
        halfWidth,
        lean,
        baseColor,
        tipColor,
      });
    }
  }

  if (profile.flowerChance <= 0) {
    return;
  }

  const flowerGate = foliageHash({ tileX: worldTileX, tileY: worldTileY, seed: 359 });
  const flowerThreshold = Math.floor(profile.flowerChance * (0.55 + density * 0.8));
  if (flowerGate > flowerThreshold) {
    return;
  }

  const flowerCount = 1 + (foliageHash({ tileX: worldTileX, tileY: worldTileY, seed: 367 }) % 2);
  for (let i = 0; i < flowerCount; i++) {
    const px = foliageHash({ tileX: worldTileX, tileY: worldTileY, seed: 373 + i * 5 });
    const py = foliageHash({ tileX: worldTileX, tileY: worldTileY, seed: 379 + i * 5 });
    const x = worldX + 4 + (px % (TILE_SIZE - 8));
    const y = worldY + 11 + (py % (TILE_SIZE - 14));
    drawTinyFlower({ g, x, y, variant: px });
  }
};

// Draw foliage blades for all tiles in a chunk
const drawChunkFoliage = ({
  g,
  chunk,
  chunkX,
  chunkY,
}: {
  g: Graphics;
  chunk: ChunkWithObjects;
  chunkX: number;
  chunkY: number;
}): void => {
  const chunkWorldX = chunkX * CHUNK_SIZE * TILE_SIZE;
  const chunkWorldY = chunkY * CHUNK_SIZE * TILE_SIZE;

  for (let localY = 0; localY < CHUNK_SIZE; localY++) {
    for (let localX = 0; localX < CHUNK_SIZE; localX++) {
      const tileIndex = localY * CHUNK_SIZE + localX;
      const tileType = (chunk.tiles[tileIndex] ?? TileType.VOID) as TileTypeValue;

      if (!GRASS_TILES.has(tileType) && !FOREST_TILES.has(tileType)) {
        continue;
      }

      drawTileFoliage({
        g,
        tileType,
        worldTileX: chunkX * CHUNK_SIZE + localX,
        worldTileY: chunkY * CHUNK_SIZE + localY,
        worldX: chunkWorldX + localX * TILE_SIZE,
        worldY: chunkWorldY + localY * TILE_SIZE,
      });
    }
  }
};

// ============================================================================
// Frame Update
// ============================================================================

// PERFORMANCE: Frame-level cache tracking
let _cachedChunksRef: ReadonlyMap<number, ChunkWithObjects> | null = null;
let _cachedChunksSize = 0;
let _cachedVisibleHash = 0;

const computeVisibleHash = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): number => {
  let hash = visibleChunks.length;
  for (let i = 0; i < visibleChunks.length; i++) {
    const vc = visibleChunks[i];
    if (vc) {
      hash = (hash * 31 + vc.x) | 0;
      hash = (hash * 31 + vc.y) | 0;
    }
  }
  return hash;
};

// Update wind shader uniforms (per-frame, zero allocation)
const updateWindUniforms = (): void => {
  if (!_windFilter) {
    return;
  }

  const camera = getCamera();
  const dims = getScreenDimensions();
  const uniformGroup = _windFilter.resources['windUniforms'] as {
    uniforms: Record<string, unknown>;
  };

  uniformGroup.uniforms['uTime'] = performance.now() * 0.001;

  // World offset: top-left corner of viewport in world pixels
  _worldOffset[0] = camera.x - dims.width / 2 / camera.zoom;
  _worldOffset[1] = camera.y - dims.height / 2 / camera.zoom;

  // Viewport size in world pixels
  _viewportSize[0] = dims.width / camera.zoom;
  _viewportSize[1] = dims.height / camera.zoom;
};

// Ensure a chunk's foliage Graphics exists and is up-to-date
const ensureChunkGraphics = ({
  chunkKey,
  chunkX,
  chunkY,
  chunk,
}: {
  chunkKey: number;
  chunkX: number;
  chunkY: number;
  chunk: ChunkWithObjects;
}): void => {
  let cached = _chunkCache.get(chunkKey);
  if (!cached) {
    const g = new Graphics();
    drawChunkFoliage({ g, chunk, chunkX, chunkY });
    container.addChild(g);
    cached = { graphics: g, tilesRef: chunk.tiles };
    _chunkCache.set(chunkKey, cached);
  } else if (cached.tilesRef !== chunk.tiles) {
    cached.graphics.clear();
    drawChunkFoliage({ g: cached.graphics, chunk, chunkX, chunkY });
    cached.tilesRef = chunk.tiles;
  }
  cached.graphics.visible = true;
};

// Rebuild visible chunk foliage Graphics when chunks change
const rebuildChunkGraphics = ({
  visibleChunks,
  loadedChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
  loadedChunks: ReadonlyMap<number, ChunkWithObjects>;
}): void => {
  _usedKeys.clear();

  for (const { x: chunkX, y: chunkY } of visibleChunks) {
    _chunkKeyTemp.x = chunkX;
    _chunkKeyTemp.y = chunkY;
    const chunkKey = chunkKeyInt(_chunkKeyTemp);
    _usedKeys.add(chunkKey);
    const chunk = loadedChunks.get(chunkKey);

    if (chunk) {
      ensureChunkGraphics({ chunkKey, chunkX, chunkY, chunk });
    }
  }

  // Hide chunks that scrolled out of view
  for (const [key, entry] of _chunkCache) {
    if (!_usedKeys.has(key)) {
      entry.graphics.visible = false;
    }
  }
};

export const drawFoliageWind = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): void => {
  const loadedChunks = getAllChunks();

  // Check if chunk Graphics need rebuild
  const visibleHash = computeVisibleHash({ visibleChunks });
  const needsRecompute =
    loadedChunks !== _cachedChunksRef ||
    loadedChunks.size !== _cachedChunksSize ||
    visibleHash !== _cachedVisibleHash;

  if (needsRecompute) {
    rebuildChunkGraphics({ visibleChunks, loadedChunks });
    _cachedChunksRef = loadedChunks;
    _cachedChunksSize = loadedChunks.size;
    _cachedVisibleHash = visibleHash;
  }

  // Update wind shader uniforms every frame
  updateWindUniforms();
};
